// Admin GDPR Page - Gestione compliance GDPR
// Permette all'admin di modificare pagine legali e gestire richieste dati

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { 
  Shield, 
  FileText, 
  Users, 
  BarChart3,
  Check,
  X,
  Download,
  Trash2,
  Save,
  Loader2,
  AlertCircle,
  Clock,
  Mail,
  UserCheck,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface LegalPage {
  page_type: string;
  title: string;
  content: string;
  version: string;
  effective_date: string;
  last_updated: string;
}

interface DeletionRequest {
  user_id: string;
  user_email: string;
  user_name: string;
  reason?: string;
  requested_at: string;
  status: string;
  processed_at?: string;
  admin_notes?: string;
}

interface GDPRStats {
  total_consent_records: number;
  analytics_accepted: number;
  marketing_accepted: number;
  pending_deletion_requests: number;
  completed_deletion_requests: number;
  pending_export_requests: number;
  total_users: number;
  terms_accepted_users: number;
  marketing_accepted_users: number;
}

interface UserConsent {
  id: string;
  email: string;
  name: string;
  role: string;
  created_at: string;
  gdpr_consents?: {
    terms_accepted?: boolean;
    terms_accepted_at?: string;
    marketing_accepted?: boolean;
    marketing_accepted_at?: string;
  };
}

const AdminGDPRPage: React.FC = () => {
  const [stats, setStats] = useState<GDPRStats | null>(null);
  const [legalPages, setLegalPages] = useState<LegalPage[]>([]);
  const [deletionRequests, setDeletionRequests] = useState<DeletionRequest[]>([]);
  const [userConsents, setUserConsents] = useState<UserConsent[]>([]);
  const [consentsPage, setConsentsPage] = useState(1);
  const [consentsTotalPages, setConsentsTotalPages] = useState(1);
  const [selectedPage, setSelectedPage] = useState<LegalPage | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editVersion, setEditVersion] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<DeletionRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchStats();
    fetchLegalPages();
    fetchDeletionRequests();
    fetchUserConsents();
  }, []);

  useEffect(() => {
    if (activeTab === 'consents') {
      fetchUserConsents();
    }
  }, [consentsPage, activeTab]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/gdpr/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
  };

  const fetchLegalPages = async () => {
    try {
      const response = await axios.get(`${API}/gdpr/legal`);
      setLegalPages(response.data);
    } catch (error) {
      console.error('Errore caricamento pagine legali:', error);
    }
  };

  const fetchUserConsents = async () => {
    try {
      const response = await axios.get(`${API}/gdpr/admin/user-consents?page=${consentsPage}&limit=10`);
      setUserConsents(response.data.users);
      setConsentsTotalPages(response.data.pages);
    } catch (error) {
      console.error('Errore caricamento consensi utenti:', error);
    }
  };

  const fetchDeletionRequests = async () => {
    try {
      const response = await axios.get(`${API}/gdpr/admin/deletion-requests`);
      setDeletionRequests(response.data);
    } catch (error) {
      console.error('Errore caricamento richieste:', error);
    }
  };

  const openEditDialog = (page: LegalPage) => {
    setSelectedPage(page);
    setEditTitle(page.title);
    setEditContent(page.content);
    setEditVersion(page.version);
    setShowEditDialog(true);
  };

  const saveLegalPage = async () => {
    if (!selectedPage) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/gdpr/admin/legal/${selectedPage.page_type}`, {
        title: editTitle,
        content: editContent,
        version: editVersion
      });
      toast.success('Pagina aggiornata con successo');
      setShowEditDialog(false);
      fetchLegalPages();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (request: DeletionRequest) => {
    setSelectedRequest(request);
    setAdminNotes('');
    setShowActionDialog(true);
  };

  const processDeletionRequest = async (action: 'approved' | 'rejected') => {
    if (!selectedRequest) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/gdpr/admin/deletion-requests/${selectedRequest.user_id}/action`, {
        status: action,
        admin_notes: adminNotes
      });
      toast.success(action === 'approved' ? 'Richiesta approvata e dati cancellati' : 'Richiesta rifiutata');
      setShowActionDialog(false);
      fetchDeletionRequests();
      fetchStats();
    } catch (error) {
      toast.error('Errore nell\'elaborazione');
    } finally {
      setLoading(false);
    }
  };

  const getPageTypeLabel = (type: string) => {
    switch (type) {
      case 'privacy_policy':
        return 'Privacy Policy';
      case 'terms_conditions':
        return 'Termini e Condizioni';
      case 'cookie_policy':
        return 'Cookie Policy';
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-gdpr-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          Gestione GDPR
        </h1>
        <p className="text-muted-foreground mt-1">
          Gestisci la compliance GDPR, pagine legali e richieste degli utenti
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Panoramica
          </TabsTrigger>
          <TabsTrigger value="consents" className="gap-2">
            <UserCheck className="h-4 w-4" />
            Consensi Utenti
          </TabsTrigger>
          <TabsTrigger value="legal" className="gap-2">
            <FileText className="h-4 w-4" />
            Pagine Legali
          </TabsTrigger>
          <TabsTrigger value="requests" className="gap-2">
            <Users className="h-4 w-4" />
            Richieste
            {stats && stats.pending_deletion_requests > 0 && (
              <Badge variant="destructive" className="ml-1">
                {stats.pending_deletion_requests}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Tab Panoramica */}
        <TabsContent value="overview" className="space-y-4">
          {/* Consensi Registrazione */}
          <h3 className="font-semibold text-lg mt-4">Consensi Registrazione Utenti</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Utenti Totali
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{stats?.total_users || 0}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  Termini Accettati
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-green-600">{stats?.terms_accepted_users || 0}</p>
                <p className="text-sm text-muted-foreground">
                  {stats?.total_users ? 
                    `${Math.round((stats.terms_accepted_users / stats.total_users) * 100)}% degli utenti` : 
                    '0%'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  Consenso Marketing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-blue-600">{stats?.marketing_accepted_users || 0}</p>
                <p className="text-sm text-muted-foreground">
                  {stats?.total_users ? 
                    `${Math.round((stats.marketing_accepted_users / stats.total_users) * 100)}% degli utenti` : 
                    '0%'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Consensi Cookie */}
          <h3 className="font-semibold text-lg mt-6">Consensi Cookie</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Consensi Cookie Totali
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{stats?.total_consent_records || 0}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Consenso Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-green-600">{stats?.analytics_accepted || 0}</p>
                <p className="text-sm text-muted-foreground">
                  {stats?.total_consent_records ? 
                    `${Math.round((stats.analytics_accepted / stats.total_consent_records) * 100)}% del totale` : 
                    '0%'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Consenso Marketing Cookie
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-blue-600">{stats?.marketing_accepted || 0}</p>
                <p className="text-sm text-muted-foreground">
                  {stats?.total_consent_records ? 
                    `${Math.round((stats.marketing_accepted / stats.total_consent_records) * 100)}% del totale` : 
                    '0%'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Richieste */}
          <h3 className="font-semibold text-lg mt-6">Richieste GDPR</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Richieste Cancellazione
                </CardTitle>
              </CardHeader>
              <CardContent className="flex items-end gap-4">
                <div>
                  <p className="text-3xl font-bold text-yellow-600">{stats?.pending_deletion_requests || 0}</p>
                  <p className="text-sm text-muted-foreground">In attesa</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-muted-foreground">{stats?.completed_deletion_requests || 0}</p>
                  <p className="text-sm text-muted-foreground">Completate</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Richieste Export Dati
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{stats?.pending_export_requests || 0}</p>
                <p className="text-sm text-muted-foreground">In elaborazione</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Consensi Utenti */}
        <TabsContent value="consents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Consensi Utenti Registrati</CardTitle>
              <CardDescription>
                Visualizza i consensi GDPR degli utenti al momento della registrazione
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Utente</th>
                      <th className="text-left py-3 px-2">Email</th>
                      <th className="text-left py-3 px-2">Ruolo</th>
                      <th className="text-center py-3 px-2">Termini</th>
                      <th className="text-center py-3 px-2">Marketing</th>
                      <th className="text-left py-3 px-2">Data Registrazione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userConsents.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-2 font-medium">{user.name}</td>
                        <td className="py-3 px-2 text-muted-foreground">{user.email}</td>
                        <td className="py-3 px-2">
                          <Badge variant="outline">
                            {user.role === 'buyer' ? 'Buyer' : 'Seller'}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-center">
                          {user.gdpr_consents?.terms_accepted ? (
                            <Check className="h-5 w-5 text-green-600 mx-auto" />
                          ) : (
                            <X className="h-5 w-5 text-red-600 mx-auto" />
                          )}
                        </td>
                        <td className="py-3 px-2 text-center">
                          {user.gdpr_consents?.marketing_accepted ? (
                            <Check className="h-5 w-5 text-green-600 mx-auto" />
                          ) : (
                            <X className="h-5 w-5 text-muted-foreground mx-auto" />
                          )}
                        </td>
                        <td className="py-3 px-2 text-muted-foreground">
                          {formatDate(user.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginazione */}
              {consentsTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConsentsPage(p => Math.max(1, p - 1))}
                    disabled={consentsPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Pagina {consentsPage} di {consentsTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConsentsPage(p => Math.min(consentsTotalPages, p + 1))}
                    disabled={consentsPage === consentsTotalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Pagine Legali */}
        <TabsContent value="legal" className="space-y-4">
          <div className="grid gap-4">
            {legalPages.map((page) => (
              <Card key={page.page_type}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{page.title}</CardTitle>
                      <CardDescription>
                        Versione {page.version} • Ultimo aggiornamento: {formatDate(page.last_updated)}
                      </CardDescription>
                    </div>
                    <Button onClick={() => openEditDialog(page)} data-testid={`edit-${page.page_type}-btn`}>
                      Modifica
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div 
                    className="prose prose-sm dark:prose-invert max-w-none max-h-40 overflow-hidden relative"
                    dangerouslySetInnerHTML={{ __html: page.content.slice(0, 500) + '...' }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-card to-transparent" />
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab Richieste */}
        <TabsContent value="requests" className="space-y-4">
          {deletionRequests.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessuna richiesta di cancellazione</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {deletionRequests.map((request) => (
                <Card key={request.user_id} className={request.status === 'pending' ? 'border-yellow-500/50' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{request.user_name}</p>
                          <Badge variant={
                            request.status === 'pending' ? 'outline' :
                            request.status === 'completed' ? 'default' : 'destructive'
                          }>
                            {request.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                            {request.status === 'pending' ? 'In attesa' :
                             request.status === 'completed' ? 'Completata' : 'Rifiutata'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{request.user_email}</p>
                        <p className="text-sm text-muted-foreground">
                          Richiesta: {formatDate(request.requested_at)}
                        </p>
                        {request.reason && (
                          <p className="text-sm mt-2">
                            <span className="font-medium">Motivo:</span> {request.reason}
                          </p>
                        )}
                        {request.admin_notes && (
                          <p className="text-sm text-muted-foreground mt-1">
                            <span className="font-medium">Note admin:</span> {request.admin_notes}
                          </p>
                        )}
                      </div>

                      {request.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => openActionDialog(request)}
                            data-testid={`reject-request-${request.user_id}`}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Rifiuta
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openActionDialog(request)}
                            data-testid={`approve-request-${request.user_id}`}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approva
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog Modifica Pagina Legale */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]" scrollable>
          <DialogHeader>
            <DialogTitle>
              Modifica {selectedPage ? getPageTypeLabel(selectedPage.page_type) : ''}
            </DialogTitle>
            <DialogDescription>
              Modifica il contenuto della pagina. Usa HTML per la formattazione.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Titolo</label>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Versione</label>
                <Input
                  value={editVersion}
                  onChange={(e) => setEditVersion(e.target.value)}
                  className="mt-1"
                  placeholder="es. 1.1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Contenuto (HTML)</label>
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="mt-1 font-mono text-sm min-h-[400px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annulla
            </Button>
            <Button onClick={saveLegalPage} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Azione Richiesta */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gestisci Richiesta di Cancellazione</DialogTitle>
            <DialogDescription>
              Utente: {selectedRequest?.user_name} ({selectedRequest?.user_email})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-destructive/10 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-destructive">Attenzione</p>
                <p className="text-muted-foreground">
                  Approvando la richiesta, i dati dell'utente verranno cancellati in modo irreversibile.
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Note (opzionali)</label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Es. Richiesta legittima, procedere con cancellazione..."
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              Annulla
            </Button>
            <Button
              variant="outline"
              className="text-red-600"
              onClick={() => processDeletionRequest('rejected')}
              disabled={loading}
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <X className="h-4 w-4 mr-2" />
              Rifiuta
            </Button>
            <Button
              variant="destructive"
              onClick={() => processDeletionRequest('approved')}
              disabled={loading}
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Check className="h-4 w-4 mr-2" />
              Approva e Cancella
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGDPRPage;
